import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  BOOKING_STATUS_LABEL,
  BOOKING_STATUS_TONE,
  MODE_LABEL,
  STATUS_LABEL,
  day,
  instant,
  mad,
  perUnit,
} from '../lib/format';
import { Badge, EmptyState, Notice, Spinner } from '../components/ui';
import type { Booking, PropertyDetail } from '../lib/types';
import HostStats from './host/HostStats';
import { t } from '../lib/i18n';

/**
 * The host's home: requests to answer, listings, and numbers.
 *
 * On a phone the three live behind tabs, and the page opens on whatever needs
 * the host: the requests when one is waiting, the listings otherwise. Answering
 * a request is two full-width buttons, never a scroll past the charts.
 */

type Section = 'requests' | 'listings' | 'stats';

export default function HostDashboard() {
  const { user, toast, reportError } = useApp();
  const [properties, setProperties] = useState<PropertyDetail[] | null>(null);
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [section, setSection] = useState<Section | null>(null);

  const load = useCallback(async () => {
    try {
      const [propertyResponse, bookingResponse] = await Promise.all([
        api.get<{ properties: PropertyDetail[] }>('/properties/mine'),
        api.get<{ bookings: Booking[] }>('/bookings?role=host'),
      ]);
      setProperties(propertyResponse.properties);
      setBookings(bookingResponse.bookings);
    } catch (error) {
      reportError(error);
      setProperties([]);
      setBookings([]);
    }
  }, [reportError]);

  useEffect(() => {
    void load();
  }, [load]);

  const respond = async (bookingId: string, action: 'accept' | 'decline') => {
    if (action === 'decline' && !window.confirm(t('Refuser cette demande ? Les dates seront libérées.'))) return;
    setBusyId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/${action}`, action === 'decline' ? { reason: 'Indisponible' } : {});
      toast(action === 'accept' ? t("Demande acceptée.") : t("Demande refusée."), 'ok');
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setBusyId(null);
    }
  };

  if (properties === null || bookings === null) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement du tableau de bord…")} />
      </div>
    );
  }

  const pending = bookings.filter((booking) => booking.status === 'PENDING');
  const others = bookings.filter((booking) => booking.status !== 'PENDING');
  const confirmed = bookings.filter((booking) => booking.status === 'CONFIRMED');
  // What the host actually receives: their own price, in full, in cash. Bedly's
  // fee is charged to the guest on top and never comes out of this number.
  const earnings = bookings
    .filter((booking) => ['CONFIRMED', 'COMPLETED'].includes(booking.status))
    .reduce((total, booking) => total + booking.amounts.hostTotalCentimes, 0);

  const current: Section = section ?? (pending.length > 0 ? 'requests' : 'listings');

  const sections: { value: Section; label: string; count?: number }[] = [
    { value: 'requests', label: t('Demandes'), count: pending.length },
    { value: 'listings', label: t('Annonces'), count: properties.length },
    { value: 'stats', label: t('Statistiques') },
  ];

  const bookingCard = (booking: Booking) => (
    <article key={booking.id} className={`card card--pad stack ${booking.status === 'PENDING' ? 'card--due' : ''}`}>
      <div className="row row--between" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div className="stack stack--tight" style={{ minWidth: 0 }}>
          <span className="strong">{booking.guest.fullName}</span>
          <span className="small muted clamp-1">{booking.property.title}</span>
        </div>
        <Badge tone={BOOKING_STATUS_TONE[booking.status]}>{BOOKING_STATUS_LABEL[booking.status]}</Badge>
      </div>
      <div className="booking-facts">
        <span>
          <span className="tiny muted">{t('Dates')}</span>
          <span className="small strong">
            {day(booking.startDate, true)} → {day(booking.endDate, true)}
          </span>
        </span>
        <span>
          <span className="tiny muted">{MODE_LABEL[booking.mode]}</span>
          <span className="small strong">{mad(booking.amounts.hostTotalCentimes)}</span>
        </span>
      </div>
      {booking.guestMessage ? (
        <p className="small guest-message">« {booking.guestMessage} »</p>
      ) : null}
      {booking.status === 'PENDING' && booking.expiresAt ? (
        <span className="tiny" style={{ color: 'var(--saff-ink)' }}>
          {t("À répondre avant le")} {instant(booking.expiresAt)}
        </span>
      ) : null}
      {booking.status === 'PENDING' ? (
        <div className="action-row">
          <button className="btn" disabled={busyId === booking.id} onClick={() => respond(booking.id, 'accept')}>
            {t("Accepter")}
          </button>
          <button
            className="btn btn--danger"
            disabled={busyId === booking.id}
            onClick={() => respond(booking.id, 'decline')}
          >
            {t("Refuser")}
          </button>
        </div>
      ) : null}
      <div className="action-row action-row--quiet">
        <Link className="btn btn--ghost btn--sm" to={`/reservations/${booking.id}`}>
          {t("Détails")}
        </Link>
        <Link className="btn btn--ghost btn--sm" to="/messages">
          {t("Message")}
        </Link>
      </div>
    </article>
  );

  return (
    <div className="page stack">
      <div className="row row--between row--wrap" style={{ gap: 12 }}>
        <div className="stack stack--tight">
          <h1 className="page-title">{t("Tableau de bord hôte")}</h1>
          <p className="muted small" style={{ margin: 0 }}>
            {t('{count} annonce(s)', { count: properties.length })} · {user?.city ? t(user.city) : t("Maroc")}
          </p>
        </div>
        <Link className="btn btn--block-mobile" to="/hote/annonces/nouvelle">
          {t("+ Nouvelle annonce")}
        </Link>
      </div>

      {user?.kycStatus !== 'APPROVED' ? (
        <Notice tone="warn">
          {t("La vérification d’identité est requise avant de publier une annonce.")}{' '}
          <Link to="/verification">{t("Vérifier maintenant")}</Link>
        </Notice>
      ) : null}

      <div className="kpis kpis--compact">
        {[
          [t("Demandes en attente"), String(pending.length), pending.length > 0 ? t("À traiter") : t("Rien à faire")],
          [t("Séjours confirmés"), String(confirmed.length), t("En cours ou à venir")],
          // Fee-on-top: the host keeps their whole price; Bedly's fee is paid by
          // the guest on top.
          [t("Revenus attendus"), mad(earnings), t("En espèces, à la remise des clés, sans commission")],
        ].map(([label, value, note]) => (
          <div key={label} className="card kpi">
            <span className="small muted">{label}</span>
            <span className="kpi__value">{value}</span>
            <span className="kpi__hint">{note}</span>
          </div>
        ))}
      </div>

      <nav className="tabs tabs--segmented" aria-label={t('Sections')}>
        {sections.map((entry) => (
          <button
            key={entry.value}
            type="button"
            className={`tab ${current === entry.value ? 'tab--on' : ''}`}
            aria-current={current === entry.value ? 'page' : undefined}
            onClick={() => setSection(entry.value)}
          >
            {entry.label}
            {entry.count ? (
              <span className={`tab__count ${entry.value === 'requests' ? 'tab__count--due' : ''}`}>
                {entry.count}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {current === 'requests' ? (
        bookings.length === 0 ? (
          <EmptyState
            title={t("Aucune demande")}
            description={t("Les demandes de réservation apparaîtront ici dès que votre annonce sera en ligne.")}
          />
        ) : (
          <div className="stack">
            {pending.length > 0 ? (
              <>
                <h2 className="section-title">{t('À traiter')}</h2>
                {pending.map(bookingCard)}
              </>
            ) : (
              <Notice tone="ok">{t('Aucune demande en attente : tout est à jour.')}</Notice>
            )}
            {others.length > 0 ? (
              <>
                <h2 className="section-title">{t("Demandes récentes")}</h2>
                {others.slice(0, 12).map(bookingCard)}
              </>
            ) : null}
          </div>
        )
      ) : null}

      {current === 'listings' ? (
        properties.length === 0 ? (
          <EmptyState
            title={t("Aucune annonce")}
            description={t("Publiez votre première annonce : photos, prix, disponibilités. Dix minutes suffisent.")}
            action={
              <Link className="btn" to="/hote/annonces/nouvelle">
                {t("Créer une annonce")}
              </Link>
            }
          />
        ) : (
          <div className="host-listings">
            {properties.map((property) => {
              const cover = property.photos.find((photo) => photo.isCover) ?? property.photos[0];
              const cheapest = property.rentalModes
                .filter((mode) => mode.isActive)
                .reduce<(typeof property.rentalModes)[number] | null>(
                  (best, mode) => (best === null || mode.priceCentimes < best.priceCentimes ? mode : best),
                  null,
                );
              return (
                <article key={property.id} className="card host-listing">
                  <Link className="host-listing__main" to={`/hote/annonces/${property.id}`}>
                    <span className="host-listing__thumb">
                      {cover ? <img src={cover.url} alt="" loading="lazy" /> : null}
                    </span>
                    <span className="stack stack--tight" style={{ minWidth: 0, gap: 4 }}>
                      <span className="row row--wrap" style={{ gap: 6 }}>
                        <Badge tone={property.status === 'ACTIVE' ? 'badge--ok' : 'badge--solid'}>
                          {STATUS_LABEL[property.status]}
                        </Badge>
                        {property.pendingRequests ? (
                          <Badge tone="badge--warn">{property.pendingRequests} {t("demande(s)")}</Badge>
                        ) : null}
                      </span>
                      <span className="strong clamp-2">{property.title}</span>
                      <span className="tiny muted clamp-1">
                        {property.neighborhood}, {t(property.city)} · {property.rooms.length} {t("chambre(s)")}
                      </span>
                      {cheapest ? (
                        <span className="small strong">
                          {mad(cheapest.priceCentimes)}
                          <span className="muted" style={{ fontWeight: 500 }}>{perUnit(cheapest.unit)}</span>
                        </span>
                      ) : (
                        <span className="small" style={{ color: 'var(--saff-ink)' }}>
                          {t("Aucun prix défini")}
                        </span>
                      )}
                    </span>
                  </Link>
                  {/*
                    Blocking dates was already possible on the listing page, but
                    nothing pointed to it — so it goes on the card itself.
                  */}
                  <div className="host-listing__actions">
                    <Link className="btn btn--ghost btn--sm" to={`/hote/annonces/${property.id}`}>
                      {t("Gérer")}
                    </Link>
                    <Link className="btn btn--ghost btn--sm" to={`/hote/annonces/${property.id}#calendrier`}>
                      {t("Bloquer des dates")}
                    </Link>
                    {property.status === 'ACTIVE' ? (
                      <Link className="btn btn--quiet btn--sm" to={`/logement/${property.id}`}>
                        {t("Voir l’annonce")}
                      </Link>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )
      ) : null}

      {current === 'stats' ? (
        properties.length > 0 ? (
          <HostStats />
        ) : (
          <EmptyState
            title={t('Pas encore de statistiques')}
            description={t('Les vues, demandes et revenus apparaîtront ici après la publication de votre première annonce.')}
          />
        )
      ) : null}
    </div>
  );
}
